import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { CARTO_DARK_TILES, CARTO_ATTRIBUTION } from '../../constants/map';
import { formatEventDate, EVENT_DATE } from '../../utils/eventDate';

/**
 * DiscoverMap — grid⇄map view for event discovery.
 *
 * Plots events that carry venue.coordinates on a dark CARTO basemap (OSM data).
 * The tile URL comes from constants/map, which appends the CARTO key when one
 * is configured — without it CARTO watermarks every tile. Uses CircleMarker
 * (pure SVG) to avoid Leaflet's default
 * marker-image asset issues. Featured events read gold, the rest flame.
 * Events without coordinates are counted in a corner note, not dropped silently.
 */
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 12); return; }
    map.fitBounds(points, { padding: [40, 40], maxZoom: 13 });
  }, [points, map]);
  return null;
}

export default function DiscoverMap({ events = [], theme = {}, isMobile = false, onEventClick }) {
  const VB = theme;
  const line = VB.borderDark || 'rgba(217,168,59,0.16)';
  const ink = VB.snowPrimary || '#FBF4E6';
  const muted = VB.snowMuted || 'rgba(251,244,230,0.72)';

  const geoEvents = useMemo(() => events.filter((e) => {
    const c = e?.venue?.coordinates;
    return c && Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng)) && (Number(c.lat) !== 0 || Number(c.lng) !== 0);
  }), [events]);

  const points = useMemo(() => geoEvents.map((e) => [Number(e.venue.coordinates.lat), Number(e.venue.coordinates.lng)]), [geoEvents]);
  const withoutGeo = events.length - geoEvents.length;

  if (geoEvents.length === 0) {
    return (
      <div style={{ borderRadius: 18, border: `1px solid ${line}`, background: VB.darkCard || '#151210', padding: '56px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: ink, marginBottom: 6, fontFamily: 'var(--font-display)' }}>No map locations for these events yet</div>
        <div style={{ fontSize: 13, color: muted, maxWidth: 340, margin: '0 auto' }}>
          These events don&apos;t have coordinates set. Switch back to grid, or widen your filters. (Geocoding events on submit would fill the map out.)
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: isMobile ? 380 : 560, borderRadius: 18, overflow: 'hidden', border: `1px solid ${line}` }}>
      <style>{`
        .leaflet-tooltip.vb-map-tip { background:#151210; border:1px solid rgba(217,168,59,0.28); border-radius:12px; padding:9px 12px; box-shadow:0 14px 40px rgba(0,0,0,0.6); }
        .leaflet-tooltip-top.vb-map-tip::before { border-top-color:#151210; }
        .leaflet-tooltip-bottom.vb-map-tip::before { border-bottom-color:#151210; }
        .leaflet-tooltip.vb-map-tip { pointer-events:none; }
      `}</style>
      <MapContainer center={points[0]} zoom={11} scrollWheelZoom={false} style={{ height: '100%', width: '100%', background: '#0A0908' }}>
        <TileLayer
          attribution={CARTO_ATTRIBUTION}
          url={CARTO_DARK_TILES}
        />
        <FitBounds points={points} />
        {geoEvents.map((ev) => {
          const c = ev.venue.coordinates;
          const featured = !!ev.isFeatured;
          const color = featured ? '#E9C061' : '#E63946';
          const priceLabel = ev.ticketInfo?.isFree ? 'Free' : (ev.startingPrice || ev.price ? `$${ev.startingPrice || ev.price}` : '');
          return (
            <CircleMarker
              key={ev._id}
              center={[Number(c.lat), Number(c.lng)]}
              radius={featured ? 10 : 8}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.85, weight: 2 }}
              eventHandlers={{
                click: () => onEventClick && onEventClick(ev),
                mouseover: (e) => { e.target.setRadius(featured ? 13 : 11); e.target.setStyle({ fillOpacity: 1 }); },
                mouseout:  (e) => { e.target.setRadius(featured ? 10 : 8); e.target.setStyle({ fillOpacity: 0.85 }); },
              }}
            >
              {/* Hover preview — Leaflet tooltips open on hover (popups need a click, which navigates away) */}
              <Tooltip className="vb-map-tip" direction="top" offset={[0, -8]} opacity={1}>
                <div style={{ minWidth: 150, maxWidth: 220 }}>
                  {featured && <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#E9C061', marginBottom: 4 }}>★ Featured</div>}
                  <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4, color: ink, fontFamily: 'var(--font-display)', lineHeight: 1.2 }}>{ev.title}</div>
                  <div style={{ fontSize: 11, color: muted }}>
                    {ev.date ? formatEventDate(ev.date, EVENT_DATE.short) : ''}
                    {ev.venue?.name && ev.venue.name !== 'TBD' ? ' · ' + ev.venue.name : (ev.venue?.city ? ' · ' + ev.venue.city : '')}
                  </div>
                  <div style={{ marginTop: 7, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    {priceLabel ? <span style={{ fontSize: 12, fontWeight: 800, color: ev.ticketInfo?.isFree ? '#12B886' : ink }}>{priceLabel}</span> : <span />}
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: '#E9C061' }}>Tap to open →</span>
                  </div>
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
      {withoutGeo > 0 && (
        <div style={{ position: 'absolute', bottom: 12, left: 12, zIndex: 500, background: 'rgba(10,9,8,0.82)', backdropFilter: 'blur(6px)', border: `1px solid ${line}`, borderRadius: 10, padding: '6px 11px', fontSize: 11, fontWeight: 600, color: muted }}>
          {geoEvents.length} on map · {withoutGeo} without a location
        </div>
      )}
    </div>
  );
}
